import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Typography,
  Alert,
} from "@mui/material";
import { useApi } from "../../utils/api";
import Loading from "../Layout/Loading";

function formatDeletedAt(client) {
  const t = client.deletedAt;
  if (t == null) return "—";
  const ms = typeof t === "number" ? t : Number(t);
  if (!Number.isFinite(ms)) return "—";
  const d = new Date(ms);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
}

/**
 * Modal listing soft-deleted clients for the coach; restore reactivates them and
 * refreshes the parent list via onClientRestored.
 */
const DeletedClientsDialog = ({ open, onClose, onClientRestored }) => {
  const { getDeletedClients, restoreDeletedClient } = useApi();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [restoringId, setRestoringId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const list = await getDeletedClients();
      setClients(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error("getDeletedClients:", e);
      setError("Could not load deleted clients.");
      setClients([]);
    } finally {
      setLoading(false);
    }
  }, [getDeletedClients]);

  useEffect(() => {
    if (open) {
      void load();
    }
  }, [open, load]);

  const handleRestore = async (userId) => {
    setRestoringId(userId);
    setError("");
    try {
      const res = await restoreDeletedClient(userId);
      if (res?.error) {
        setError(res.error);
        return;
      }
      setClients((prev) => prev.filter((c) => c.id !== userId));
      onClientRestored?.();
    } catch (e) {
      console.error("restoreDeletedClient:", e);
      setError("Could not restore client.");
    } finally {
      setRestoringId(null);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper">
      <DialogTitle>Deleted clients</DialogTitle>
      <DialogContent dividers sx={{ pt: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Restore a client to make them active again. They can sign in once
          restored.
        </Typography>

        {error ? (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        ) : null}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <Loading />
          </Box>
        ) : clients.length === 0 ? (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography color="text.secondary">
              No deleted clients. Removed clients appear here.
            </Typography>
          </Paper>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {clients.map((client) => (
              <Paper
                key={client.id}
                variant="outlined"
                sx={{
                  p: 2,
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle1" noWrap>
                    {client.name || "Unnamed"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {client.email || "—"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Deleted {formatDeletedAt(client)}
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  disabled={restoringId === client.id}
                  onClick={() => handleRestore(client.id)}
                >
                  {restoringId === client.id ? "Restoring…" : "Restore"}
                </Button>
              </Paper>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeletedClientsDialog;
